import React, { createContext, useContext, useState, useEffect } from 'react';
import { useUserContext } from './UserContext';
import { TransactionType, OptionalTransactionType } from '../global/types';

export interface SmartRule {
    id: string;
    keyword: string;
    categoryId: string; // ID of the subcategory
    categoryType: TransactionType | OptionalTransactionType;
    matchType: 'contains' | 'exact' | 'starts_with';
}

interface SmartRulesContextType {
    rules: SmartRule[];
    addRule: (rule: Omit<SmartRule, 'id'>) => void;
    deleteRule: (id: string) => void;
    updateRule: (id: string, rule: Partial<SmartRule>) => void;
    findMatchingRule: (description: string) => SmartRule | undefined;
}

const SmartRulesContext = createContext<SmartRulesContextType | undefined>(undefined);

export const useSmartRules = () => {
    const context = useContext(SmartRulesContext);
    if (!context) {
        throw new Error('useSmartRules must be used within a SmartRulesProvider');
    }
    return context;
};

export const SmartRulesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { userId } = useUserContext();
    const [rules, setRules] = useState<SmartRule[]>([]);

    useEffect(() => {
        if (!userId) {
            setRules([]);
            return;
        }

        // Load rules from localStorage
        const savedRules = localStorage.getItem(`smart_rules_${userId}`);
        if (savedRules) {
            try {
                setRules(JSON.parse(savedRules));
            } catch (e) {
                console.error("Failed to parse smart rules", e);
                setRules([]);
            }
        }
    }, [userId]);

    const saveRules = (newRules: SmartRule[]) => {
        setRules(newRules);
        if (userId) {
            localStorage.setItem(`smart_rules_${userId}`, JSON.stringify(newRules));
        }
    };

    const addRule = (rule: Omit<SmartRule, 'id'>) => {
        const newRule: SmartRule = {
            ...rule,
            id: crypto.randomUUID(),
        };
        saveRules([...rules, newRule]);
    };

    const deleteRule = (id: string) => {
        saveRules(rules.filter(r => r.id !== id));
    };

    const updateRule = (id: string, updates: Partial<SmartRule>) => {
        saveRules(rules.map(r => r.id === id ? { ...r, ...updates } : r));
    };

    const findMatchingRule = (description: string): SmartRule | undefined => {
        if (!description) return undefined;
        const normalizedDesc = description.toLowerCase();

        // Sort by length of keyword (descending) to match specific rules first
        const sortedRules = [...rules].sort((a, b) => b.keyword.length - a.keyword.length);

        return sortedRules.find(rule => {
            const keyword = rule.keyword.toLowerCase();
            if (rule.matchType === 'exact') return normalizedDesc === keyword;
            if (rule.matchType === 'starts_with') return normalizedDesc.startsWith(keyword);
            return normalizedDesc.includes(keyword); // contains
        });
    };

    return (
        <SmartRulesContext.Provider value={{ rules, addRule, deleteRule, updateRule, findMatchingRule }}>
            {children}
        </SmartRulesContext.Provider>
    );
};
